'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { DollarSign, Users, Bitcoin, Zap, Info, RefreshCw, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

// BACKEND INTEGRATION: Replace with live FFCT and BTC prices from token API
const FFCT_PRICE_USD = 0.000369;
const BTC_PRICE_USD = 84790;
const FFCT_TX_FEE_RATE = 0.0015;
const FEE_ADDRESS = '37t7eXYbqedv6N6MJC9wgL6N1Ea4FLazc6';

const TIP_PRESETS = [
  { pct: 10, label: '10%', note: 'Minimum' },
  { pct: 15, label: '15%', note: 'Standard' },
  { pct: 18, label: '18%', note: 'Good' },
  { pct: 20, label: '20%', note: 'Great' },
  { pct: 25, label: '25%', note: 'Generous' },
];

interface FormValues {
  subtotal: string;
  customTip: string;
  splitCount: number;
}

export default function TipCalculatorContent() {
  const [selectedPreset, setSelectedPreset] = useState<number | null>(20);
  const [copiedAddress, setCopiedAddress] = useState(false);

  const { register, watch, control, setValue, formState: { errors } } = useForm<FormValues>({
    defaultValues: { subtotal: '', customTip: '', splitCount: 1 },
  });

  const subtotalRaw = watch('subtotal');
  const customTipRaw = watch('customTip');
  const splitCount = watch('splitCount');

  const subtotal = parseFloat(subtotalRaw) || 0;
  const tipPct =
    customTipRaw !== ''
      ? parseFloat(customTipRaw) || 0
      : selectedPreset ?? 0;

  const tipAmount = (subtotal * tipPct) / 100;
  const totalUsd = subtotal + tipAmount;
  const perPersonUsd = splitCount > 1 ? totalUsd / splitCount : totalUsd;
  const perPersonTip = splitCount > 1 ? tipAmount / splitCount : tipAmount;

  const ffctEquivalent = totalUsd > 0 ? totalUsd / FFCT_PRICE_USD : 0;
  const btcEquivalent = totalUsd > 0 ? totalUsd / BTC_PRICE_USD : 0;
  const ffctTxFee = ffctEquivalent * FFCT_TX_FEE_RATE;
  const ffctAfterFee = ffctEquivalent - ffctTxFee;

  const perPersonFfct = splitCount > 1 ? perPersonUsd / FFCT_PRICE_USD : ffctEquivalent;
  const perPersonBtc = splitCount > 1 ? perPersonUsd / BTC_PRICE_USD : btcEquivalent;

  const handlePresetClick = (pct: number) => {
    setSelectedPreset(pct);
    setValue('customTip', '');
  };

  const handleCustomTipChange = () => {
    setSelectedPreset(null);
  };

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(FEE_ADDRESS);
    setCopiedAddress(true);
    toast.success('Fee address copied');
    setTimeout(() => setCopiedAddress(false), 2000);
  };

  const handleReset = () => {
    setValue('subtotal', '');
    setValue('customTip', '');
    setValue('splitCount', 1);
    setSelectedPreset(20);
    toast.info('Calculator reset');
  };

  const formatFfct = (val: number) => {
    if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(2)}M`;
    if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
    return val.toFixed(0);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Tip Calculator</h1>
          <p className="text-sm text-zinc-400 mt-1">
            Calculate tips in USD and see the equivalent in FFCT &amp; Bitcoin
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="badge-gold text-xs">
            <Zap size={10} />
            FFCT ${FFCT_PRICE_USD.toFixed(6)}
          </span>
          <span className="method-badge text-xs">
            <Bitcoin size={10} />
            BTC ${BTC_PRICE_USD.toLocaleString()}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-5 gap-6">
        {/* Left: Input panel */}
        <div className="lg:col-span-3 xl:col-span-3 2xl:col-span-3 flex flex-col gap-5">

          {/* Subtotal input */}
          <div className="rounded-xl border border-zinc-800 bg-card p-5 flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-white">Order Details</h2>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-zinc-400">Order Subtotal (USD)</label>
              <p className="text-xs text-zinc-500">Enter the subtotal before tax and tip</p>
              <div className="relative">
                <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  {...register('subtotal', {
                    pattern: { value: /^\d*\.?\d{0,2}$/, message: 'Enter a valid dollar amount' },
                  })}
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  className="input-base pl-9 text-lg font-mono font-semibold"
                />
              </div>
              {errors.subtotal && (
                <p className="text-xs text-red-400">{errors.subtotal.message}</p>
              )}
            </div>
          </div>

          {/* Tip % selector */}
          <div className="rounded-xl border border-zinc-800 bg-card p-5 flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-white">Tip Percentage</h2>

            {/* Preset buttons */}
            <div className="grid grid-cols-5 gap-2">
              {TIP_PRESETS.map((preset) => (
                <button
                  key={`tip-preset-${preset.pct}`}
                  onClick={() => handlePresetClick(preset.pct)}
                  className={`flex flex-col items-center gap-0.5 py-3 rounded-xl border text-center transition-all duration-150 active:scale-95 ${
                    selectedPreset === preset.pct && customTipRaw === '' ?'bg-btcgold-500/15 border-btcgold-500/40 text-btcgold-400' :'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white'
                  }`}
                >
                  <span className="text-sm font-bold">{preset.label}</span>
                  <span className="text-xs opacity-60">{preset.note}</span>
                </button>
              ))}
            </div>

            {/* Custom tip input */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-zinc-400">Custom Tip %</label>
              <div className="relative">
                <input
                  {...register('customTip', {
                    onChange: handleCustomTipChange,
                    pattern: { value: /^\d*\.?\d{0,1}$/, message: 'Enter a valid percentage' },
                  })}
                  type="text"
                  inputMode="decimal"
                  placeholder="Enter custom %"
                  className="input-base pr-8 font-mono"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm font-mono">%</span>
              </div>
              {errors.customTip && (
                <p className="text-xs text-red-400">{errors.customTip.message}</p>
              )}
            </div>

            {/* Tip summary inline */}
            {subtotal > 0 && (
              <div className="rounded-lg bg-zinc-800/60 border border-zinc-700 p-3 flex items-center justify-between">
                <span className="text-xs text-zinc-400">
                  {tipPct}% tip on ${subtotal.toFixed(2)}
                </span>
                <span className="text-sm font-mono font-bold text-white">
                  +${tipAmount.toFixed(2)}
                </span>
              </div>
            )}
          </div>

          {/* Bill splitter */}
          <div className="rounded-xl border border-zinc-800 bg-card p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white">Split the Bill</h2>
              <span className="text-xs text-zinc-500">Optional</span>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-zinc-400">Number of People</label>
              <p className="text-xs text-zinc-500">Divide the total evenly among your group</p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setValue('splitCount', Math.max(1, splitCount - 1))}
                  className="w-10 h-10 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-300 hover:bg-zinc-700 transition-colors text-lg font-bold active:scale-95"
                  aria-label="Decrease split count"
                >
                  −
                </button>
                <div className="flex items-center gap-2 flex-1 justify-center">
                  <Users size={16} className="text-zinc-500" />
                  <span className="text-xl font-bold font-mono text-white tabular-nums">{splitCount}</span>
                  <span className="text-sm text-zinc-500">{splitCount === 1 ? 'person' : 'people'}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setValue('splitCount', Math.min(20, splitCount + 1))}
                  className="w-10 h-10 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-300 hover:bg-zinc-700 transition-colors text-lg font-bold active:scale-95"
                  aria-label="Increase split count"
                >
                  +
                </button>
              </div>
            </div>

            {splitCount > 1 && subtotal > 0 && (
              <div className="rounded-lg bg-zinc-800/60 border border-zinc-700 p-3 flex items-center justify-between">
                <span className="text-xs text-zinc-400">Per person</span>
                <div className="text-right">
                  <p className="text-sm font-mono font-bold text-white">${perPersonUsd.toFixed(2)}</p>
                  <p className="text-xs font-mono text-zinc-500">incl. ${perPersonTip.toFixed(2)} tip</p>
                </div>
              </div>
            )}
          </div>

          {/* Reset */}
          <button
            type="button"
            onClick={handleReset}
            className="btn-ghost w-full justify-center"
          >
            <RefreshCw size={14} />
            Reset Calculator
          </button>
        </div>

        {/* Right: Results panel */}
        <div className="lg:col-span-2 xl:col-span-2 2xl:col-span-2 flex flex-col gap-4">

          {/* USD Summary */}
          <div className="rounded-xl border border-btcgold-500/25 bg-btcgold-500/5 p-5 flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-white">Order Summary</h2>

            <div className="flex flex-col gap-3">
              {[
                { label: 'Subtotal', value: subtotal > 0 ? `$${subtotal.toFixed(2)}` : '—', muted: true },
                { label: `Tip (${tipPct}%)`, value: subtotal > 0 ? `$${tipAmount.toFixed(2)}` : '—', muted: true },
              ].map((row) => (
                <div key={`summary-${row.label}`} className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">{row.label}</span>
                  <span className={`text-sm font-mono font-medium ${row.muted ? 'text-zinc-300' : 'text-white'}`}>
                    {row.value}
                  </span>
                </div>
              ))}
              <div className="border-t border-zinc-800 pt-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-white">Total</span>
                <span className="text-xl font-bold font-mono text-btcgold-400 tabular-nums">
                  {subtotal > 0 ? `$${totalUsd.toFixed(2)}` : '—'}
                </span>
              </div>
              {splitCount > 1 && subtotal > 0 && (
                <div className="flex items-center justify-between bg-zinc-800/60 rounded-lg px-3 py-2">
                  <span className="text-xs text-zinc-400">Each person pays</span>
                  <span className="text-sm font-mono font-bold text-white">${perPersonUsd.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* FFCT Equivalent */}
          <div className="rounded-xl border border-zinc-800 bg-card p-5 flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Zap size={16} className="text-btcgold-400" />
              <h2 className="text-sm font-semibold text-white">FFCT Equivalent</h2>
            </div>

            <div className="flex flex-col gap-3">
              <div className="rounded-lg bg-zinc-800/60 border border-zinc-700 p-4 text-center">
                <p className="text-xs text-zinc-500 mb-1">
                  {splitCount > 1 ? `Per person (${splitCount} people)` : 'Total in FFCT'}
                </p>
                <p className="text-2xl font-bold font-mono text-btcgold-400 tabular-nums">
                  {subtotal > 0
                    ? `${formatFfct(splitCount > 1 ? perPersonFfct : ffctEquivalent)} FFCT`
                    : '— FFCT'}
                </p>
                {subtotal > 0 && (
                  <p className="text-xs text-zinc-500 mt-1 font-mono">
                    @ $0.000369/FFCT
                  </p>
                )}
              </div>

              {subtotal > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-500">FFCT amount</span>
                    <span className="text-xs font-mono text-zinc-300">{formatFfct(ffctEquivalent)} FFCT</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-500">Tx fee (0.15%)</span>
                    <span className="text-xs font-mono text-red-400">−{formatFfct(ffctTxFee)} FFCT</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-zinc-800 pt-2">
                    <span className="text-xs text-zinc-400 font-medium">After fee</span>
                    <span className="text-xs font-mono text-green-400 font-semibold">{formatFfct(ffctAfterFee)} FFCT</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* BTC Equivalent */}
          <div className="rounded-xl border border-zinc-800 bg-card p-5 flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Bitcoin size={16} className="text-orange-400" />
              <h2 className="text-sm font-semibold text-white">Bitcoin Equivalent</h2>
            </div>

            <div className="rounded-lg bg-zinc-800/60 border border-zinc-700 p-4 text-center">
              <p className="text-xs text-zinc-500 mb-1">
                {splitCount > 1 ? `Per person (${splitCount} people)` : 'Total in BTC'}
              </p>
              <p className="text-xl font-bold font-mono text-orange-400 tabular-nums">
                {subtotal > 0
                  ? `${(splitCount > 1 ? perPersonBtc : btcEquivalent).toFixed(8)} BTC`
                  : '— BTC'}
              </p>
              {subtotal > 0 && (
                <p className="text-xs text-zinc-500 mt-1 font-mono">
                  @ ${BTC_PRICE_USD.toLocaleString()}/BTC
                </p>
              )}
            </div>

            {subtotal > 0 && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-zinc-800/40 border border-zinc-700">
                <Info size={13} className="text-zinc-500 flex-shrink-0" />
                <p className="text-xs text-zinc-500 leading-relaxed">
                  BTC amount shown for reference. Actual payment via Lightning or Flexa app at point of sale.
                </p>
              </div>
            )}
          </div>

          {/* Fee address note */}
          <div className="rounded-xl border border-zinc-800 bg-card p-4 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-btcgold-400" />
              <span className="text-xs font-semibold text-white">FFCT Transaction Fee Destination</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0 bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5">
                <span className="font-mono text-xs text-btcgold-300 break-all">{FEE_ADDRESS}</span>
              </div>
              <button
                onClick={handleCopyAddress}
                className="flex-shrink-0 p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 transition-all duration-150 active:scale-95"
                aria-label="Copy fee address"
              >
                {copiedAddress ? (
                  <Check size={12} className="text-green-400" />
                ) : (
                  <Copy size={12} className="text-zinc-400" />
                )}
              </button>
            </div>
            <p className="text-xs text-zinc-500">
              0.15% of every FFCT transaction is automatically routed to this Bitcoin address.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}